import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { listGeographyNodes, type GeoNodeOption } from '../../api/brandingWorkOrderApi.js';
import { Field, s } from '../mdm/_mdmKit.js';

export interface GeoChain {
  readonly stateId: string;
  readonly districtId: string;
  readonly tehsilId: string;
  readonly cityId: string;
}

export const EMPTY_CHAIN: GeoChain = {
  stateId: '', districtId: '', tehsilId: '', cityId: '',
};

const LEVELS = [
  { key: 'stateId',    code: 'GEO_STATE',    label: 'State' },
  { key: 'districtId', code: 'GEO_DISTRICT', label: 'District' },
  { key: 'tehsilId',   code: 'GEO_TEHSIL',   label: 'Sub-district' },
  { key: 'cityId',     code: 'GEO_CITY',     label: 'City / Village' },
] as const;

interface Props {
  readonly value: GeoChain;
  readonly onChange: (next: GeoChain) => void;
  readonly disabled?: boolean;
  /** t626_geography_scope stops at sub-district, so a scope picker must not
   *  offer City. An address picker goes all the way, because the pincode hangs
   *  off the city row. */
  readonly deepest?: 'tehsilId' | 'cityId';
  /** Shortlist for City, used when a pincode has already narrowed it. Without
   *  this the list is every city in the sub-district, which contradicts the
   *  pincode the operator just typed. */
  readonly cityOptions?: readonly { readonly id: string; readonly name: string }[];
  readonly idPrefix?: string;
}

/**
 * State → District → Sub-district → City, every level picked from a list.
 *
 * Each list is fetched once per parent and kept. An earlier version depended on
 * the whole chain in a single effect, so choosing a district re-fetched the
 * states too; with two pickers on screen that was enough to trip the 60
 * requests/minute throttle and the page failed with EX-X-13.
 */
export function VendorGeographyPicker({
  value, onChange, disabled, deepest, cityOptions, idPrefix = 'vp-geo',
}: Props): ReactNode {
  const { token } = useAuth();
  /** Keyed `${levelCode}:${parentId}` — the same key as `asked` — so going back
   *  to a parent already fetched shows that parent's children, not whichever
   *  list happened to be fetched last. */
  const [options, setOptions] = useState<Record<string, readonly GeoNodeOption[]>>({});
  const [error, setError] = useState<string | null>(null);
  /** Keys already requested, so a re-render never asks for the same list twice. */
  const asked = useRef<Set<string>>(new Set());

  const levels = LEVELS.slice(0, deepest === 'tehsilId' ? 3 : LEVELS.length);

  const load = (code: string, parentId?: string) => {
    const key = `${code}:${parentId ?? ''}`;
    if (asked.current.has(key)) return;
    asked.current.add(key);
    listGeographyNodes(code, parentId, token)
      .then((r) => setOptions((o) => ({ ...o, [key]: r })))
      .catch((e) => { asked.current.delete(key); setError((e as Error).message); });
  };

  // States: once per session.
  useEffect(() => {
    load('GEO_STATE');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Each child list: only when its own parent changes.
  useEffect(() => {
    if (value.stateId) load('GEO_DISTRICT', value.stateId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, value.stateId]);

  useEffect(() => {
    if (value.districtId) load('GEO_TEHSIL', value.districtId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, value.districtId]);

  useEffect(() => {
    if (value.tehsilId && deepest !== 'tehsilId' && !cityOptions) load('GEO_CITY', value.tehsilId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, value.tehsilId, cityOptions]);

  const choose = (index: number, id: string) => {
    const draft: Record<string, string> = { ...value };
    LEVELS.forEach((l, i) => {
      if (i === index) draft[l.key] = id;
      else if (i > index) draft[l.key] = '';
    });
    onChange({
      stateId: draft.stateId, districtId: draft.districtId,
      tehsilId: draft.tehsilId, cityId: draft.cityId,
    });
  };

  return (
    <>
      {error ? <span role="alert" style={{ color: 'var(--color-danger)' }}>{error}</span> : null}
      {levels.map((l, i) => {
        const shortlisted = l.key === 'cityId' && cityOptions !== undefined;
        // A pincode can span two sub-districts, so its shortlist stands on its
        // own and does not wait for the level above to be filled.
        const parentChosen = i === 0 || shortlisted || value[LEVELS[i - 1].key] !== '';
        const parentId = i === 0 ? '' : value[LEVELS[i - 1].key];
        const opts = shortlisted ? cityOptions : (options[`${l.code}:${parentId}`] ?? []);
        return (
          <Field key={l.code} id={`${idPrefix}-${l.key}`} label={l.label}>
            <select
              id={`${idPrefix}-${l.key}`}
              aria-label={`vendor-${l.key}`}
              data-testid={`vendor-geo-${l.key}`}
              style={s.input}
              value={value[l.key]}
              disabled={disabled || !parentChosen}
              onChange={(e) => choose(i, e.target.value)}
            >
              <option value="">{`Select ${l.label}`}</option>
              {opts.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </Field>
        );
      })}
    </>
  );
}
